import { ColumnDisplayOptions } from '../../map/colorScale';

// The colours of a report's columns, kept with the report's definition in
// a list the backend stores as it comes and never reads. An entry names its
// column by the column's own definition rather than by its place, so the
// colours follow the column when others are added, removed or moved, and
// any column can have them, even one that is a bare name or number
// ("ChildCount", { AdditionalColumn: 12 }). What the viewer adds inside a
// column (its stored link) is left out of the name, so storing the column
// keeps its colours.

// a column is taken as the JSON it is: the pages type it two ways
export type ColumnColors = { column: unknown, display_options: ColumnDisplayOptions };

// the report's definition, whatever type the page gives it, with the list
type Definition = { column_colors?: ColumnColors[] };

// the keys the report viewer adds inside a column, not part of its definition
const VIEWER_KEYS = new Set(['additional_column']);

// the same text for the same definition, whatever the order of its keys
function canonical(value: unknown): string {
	if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
	if (value && typeof value === 'object') {
		const entries = Object.entries(value).filter(([, v]) => v !== undefined).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
		return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(',')}}`;
	}
	return JSON.stringify(value);
}

/** The column without what the viewer added inside it. */
function bare(column: unknown): unknown {
	if (!column || typeof column !== 'object') return column;
	const [tag, inner] = Object.entries(column)[0];
	if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return column;
	return { [tag]: Object.fromEntries(Object.entries(inner).filter(([key]) => !VIEWER_KEYS.has(key))) };
}

/** What names a column among the report's: its definition, as text. */
export function columnKey(column: unknown): string {
	return canonical(bare(column));
}

/** The colours set on a column of the report, if any. */
export function columnDisplayOptionsOf(report: object | undefined, column: unknown): ColumnDisplayOptions | undefined {
	if (!report || column === undefined) return undefined;
	const key = columnKey(column);
	return (report as Definition).column_colors?.find(entry => columnKey(entry.column) === key)?.display_options;
}

/** Sets these colours on the column, in place, or clears them. */
export function setColumnDisplayOptions(report: object, column: unknown, options: ColumnDisplayOptions | undefined): void {
	const definition = report as Definition;
	const key = columnKey(column);
	const kept = (definition.column_colors ?? []).filter(entry => columnKey(entry.column) !== key);
	if (options) kept.push({ column: bare(column), display_options: options });
	if (kept.length > 0) definition.column_colors = kept;
	else delete definition.column_colors;
}
